/**
 * TanStack Query hooks for Tasks.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CalendarTimedResponse,
  CrewTaskDetailResponse,
  CrewTodayResponse,
  DispatchAssignment,
  DispatchDayResponse,
  PendingTasksResponse,
  Task,
  TaskAssignmentsSetInput,
  TaskConfirmInput,
  TaskCreateInput,
  TaskListQuery,
  TaskListResponse,
  TaskReassignInput,
  TaskStatusTransitionInput,
  TaskUpdateInput,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { enqueueMutation, isNetworkError, newIdempotencyKey } from '@/lib/offlineQueue';

const KEY = ['tasks'] as const;

function queryString(q: TaskListQuery): string {
  const params = new URLSearchParams();
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  if (q.project_id) params.set('project_id', q.project_id);
  if (q.status) params.set('status', q.status);
  if (q.type) params.set('type', q.type);
  if (q.crew_member_id) params.set('crew_member_id', q.crew_member_id);
  if (q.from_date) params.set('from_date', q.from_date);
  if (q.to_date) params.set('to_date', q.to_date);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useTasks(
  query: TaskListQuery = { limit: 50, offset: 0 },
  options?: { enabled?: boolean },
) {
  return useQuery<TaskListResponse>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => apiCall<TaskListResponse>(`/tasks${queryString(query)}`),
    enabled: options?.enabled,
  });
}

/**
 * PM-requested tasks awaiting a supervisor's confirm, across all projects
 * (Calendar Phase 4b) — powers the supervisor/admin Home approval card. Shares
 * the ['tasks'] key prefix, so create/confirm invalidations refresh it.
 */
export function usePendingTasks(enabled = true) {
  return useQuery<PendingTasksResponse>({
    queryKey: [...KEY, 'pending'],
    enabled,
    queryFn: () => apiCall<PendingTasksResponse>('/tasks/pending'),
  });
}

export function useTask(id: string | undefined) {
  return useQuery<Task>({
    queryKey: [...KEY, 'detail', id],
    enabled: !!id,
    queryFn: () => apiCall<Task>(`/tasks/${id}`),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation<Task, Error, TaskCreateInput>({
    mutationFn: (body) => apiCall<Task>('/tasks', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateTask(id: string) {
  const qc = useQueryClient();
  return useMutation<Task, Error, TaskUpdateInput>({
    mutationFn: (body) => apiCall<Task>(`/tasks/${id}`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
      // The crew task-detail screen reads its own query — refresh it so a
      // supervisor's edit (title / crew size / duration) shows there too.
      qc.invalidateQueries({ queryKey: ['crew-task-detail'] });
    },
  });
}

/**
 * Confirm a PM-requested (pending) task into committed work (Calendar Phase 4b).
 * Supervisor/admin only; idempotent on an already-committed task.
 */
export function useConfirmTask(id: string) {
  const qc = useQueryClient();
  return useMutation<
    Task,
    Error,
    TaskConfirmInput | void,
    { snapshots: Array<[readonly unknown[], CalendarTimedResponse | undefined]> }
  >({
    // Body carries an optional schedule override (accept the requested time, or
    // commit a different time/day). Always send an object so the body validates.
    mutationFn: (body) =>
      apiCall<Task>(`/tasks/${id}/confirm`, { method: 'POST', body: body ?? {} }),
    // Optimistic (demo #5): flip the pending (dashed) calendar block to committed
    // instantly so the supervisor sees the confirm without the refetch lag. The
    // timed feed over-fetches ±1 period, so patch EVERY cached range.
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['calendar-timed'] });
      const snapshots = qc.getQueriesData<CalendarTimedResponse>({ queryKey: ['calendar-timed'] });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData<CalendarTimedResponse>(key, {
          ...data,
          blocks: data.blocks.map((b) =>
            b.kind === 'task' && b.id === id
              ? {
                  ...b,
                  pending: false,
                  status: 'scheduled',
                  scheduled_start: body?.scheduled_start ?? b.scheduled_start,
                  scheduled_end: body?.scheduled_end ?? b.scheduled_end,
                }
              : b,
          ),
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshots ?? []) qc.setQueryData(key, data);
    },
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, 'detail', id], updated);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['calendar-timed'] });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-schedule'] });
    },
  });
}

/**
 * Remove a task from the work plan (supervisor task editor). The API refuses
 * with a 409 once the task is underway; the caller surfaces that message.
 * Invalidates every view that lists tasks so the row disappears everywhere.
 */
export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiCall<void>(`/tasks/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-schedule'] });
      qc.invalidateQueries({ queryKey: ['crew-task-detail'] });
    },
  });
}

/** A transition accepted into the offline queue instead of sent (B.1). */
export interface QueuedTransition {
  queued: true;
}
export type TransitionResult = Task | QueuedTransition;

export function isQueuedResult(r: TransitionResult): r is QueuedTransition {
  return typeof r === 'object' && r !== null && 'queued' in r && r.queued === true;
}

export function useTransitionTask(id: string) {
  const qc = useQueryClient();
  return useMutation<TransitionResult, Error, TaskStatusTransitionInput>({
    mutationFn: async (body) => {
      // Stamp field-time at tap, not at send: a queued "Complete" that syncs
      // an hour later must record when it actually happened (ADR-0014).
      const payload = { ...body, occurred_at: body.occurred_at ?? new Date().toISOString() };
      const key = newIdempotencyKey();
      try {
        return await apiCall<Task>(`/tasks/${id}/transition`, {
          method: 'POST',
          body: payload,
          headers: { 'Idempotency-Key': key },
        });
      } catch (err) {
        if (!isNetworkError(err)) throw err;
        // Dead network — the tap that matters most must not be lost (B.1).
        await enqueueMutation({
          path: `/tasks/${id}/transition`,
          method: 'POST',
          body: payload,
          idempotencyKey: key,
          label: `Task update · ${payload.to.replace(/_/g, ' ')}`,
        });
        return { queued: true };
      }
    },
    onSuccess: (updated, vars) => {
      if (isQueuedResult(updated)) {
        // Offline: advance the crew's local truth so their screens keep
        // matching reality; the server catches up on the replay.
        qc.setQueriesData<CrewTodayResponse>({ queryKey: ['crew-today'] }, (old) =>
          old
            ? {
                ...old,
                tasks: old.tasks.map((t) =>
                  t.id === id ? { ...t, status: vars.to } : t,
                ),
              }
            : old,
        );
        qc.setQueryData<CrewTaskDetailResponse>(['crew-task-detail', id], (old) =>
          old ? { ...old, status: vars.to } : old,
        );
        return;
      }
      qc.setQueryData([...KEY, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: KEY });
      // A status transition shows up in every view that renders the task
      // — crew Today, the crew task-detail timeline, dispatch board, and the
      // public status page.
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-task-detail'] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['public-status'] });
      // The rollup may advance the project's status (VCS-102) — refresh the
      // project detail/badges too.
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Extend the task's ending-soon reminder by 15/30/45/60 min (demo #8).
 * Schedules one more nudge after the interval; does NOT move the committed end
 * time. Best-effort — no offline queue, since it's a convenience reminder.
 */
export function useExtendTaskReminder(id: string) {
  return useMutation<{ ok: boolean; remind_at: string }, Error, number>({
    mutationFn: (minutes) =>
      apiCall<{ ok: boolean; remind_at: string }>(`/tasks/${id}/extend-reminder`, {
        method: 'POST',
        body: { minutes },
      }),
  });
}

/**
 * Move a task between lanes in a cached dispatch day. Pure — used by the
 * optimistic reassign below. Capacity/hours in the header are left to the
 * refetch; the bar's position is the thing that must not snap back.
 */
function moveTaskInDispatchDay(
  day: DispatchDayResponse,
  taskId: string,
  body: TaskReassignInput,
): DispatchDayResponse {
  const fromId = body.from_crew_member_id ?? null;
  const toId = body.to_crew_member_id ?? null;

  let moved: DispatchAssignment | null = null;
  const removed = day.crew_rows.map((r) => {
    if (!fromId || r.crew_member.id !== fromId) return r;
    moved = r.assignments.find((a) => a.task_id === taskId) ?? null;
    return { ...r, assignments: r.assignments.filter((a) => a.task_id !== taskId) };
  });
  const rows = removed.map((r) => {
    if (!toId || r.crew_member.id !== toId) return r;
    const t = day.tasks_by_id[taskId];
    const assignment: DispatchAssignment = moved ?? {
      task_id: taskId,
      is_lead: false,
      scheduled_start: t?.scheduled_start ?? null,
      scheduled_end: t?.scheduled_end ?? null,
      truck_label: t?.truck_label ?? null,
    };
    return { ...r, assignments: [...r.assignments, assignment] };
  });

  const unassigned = toId
    ? day.unassigned_task_ids.filter((id) => id !== taskId)
    : fromId
      ? [...day.unassigned_task_ids.filter((id) => id !== taskId), taskId]
      : day.unassigned_task_ids;

  return { ...day, crew_rows: rows, unassigned_task_ids: unassigned };
}

/**
 * Reassign a task between crew members. Used by the dispatch board
 * drag-drop. Optimistic: the invalidate-only version visibly snapped the
 * bar back to its old lane for the refetch round-trip on every single drag
 * — the board's core interaction read as a failure.
 */
export function useReassignTask() {
  const qc = useQueryClient();
  return useMutation<
    Task,
    Error,
    { taskId: string; body: TaskReassignInput },
    { snapshots: Array<[readonly unknown[], DispatchDayResponse | undefined]> }
  >({
    mutationFn: ({ taskId, body }) =>
      apiCall<Task>(`/tasks/${taskId}/reassign`, { method: 'POST', body }),
    onMutate: async ({ taskId, body }) => {
      await qc.cancelQueries({ queryKey: ['dispatch'] });
      const snapshots = qc.getQueriesData<DispatchDayResponse>({ queryKey: ['dispatch'] });
      for (const [key, day] of snapshots) {
        if (day) qc.setQueryData(key, moveTaskInDispatchDay(day, taskId, body));
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, day] of ctx?.snapshots ?? []) qc.setQueryData(key, day);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Set a task's full crew set (SCH.6 assign-crew picker). The API reconciles
 * inserts/removals + lead and notifies the resulting crew.
 */
export function useSetTaskAssignments(taskId: string) {
  const qc = useQueryClient();
  return useMutation<Task, Error, TaskAssignmentsSetInput>({
    mutationFn: (body) =>
      apiCall<Task>(`/tasks/${taskId}/assignments`, { method: 'PUT', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-schedule'] });
    },
  });
}

/** Book (or clear) a task's vehicle — used by the dispatch vehicle panel. */
export function useAssignVehicle() {
  const qc = useQueryClient();
  return useMutation<Task, Error, { taskId: string; vehicleId: string | null }>({
    mutationFn: ({ taskId, vehicleId }) =>
      apiCall<Task>(`/tasks/${taskId}`, {
        method: 'PATCH',
        body: { vehicle_id: vehicleId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['crew-today'] });
      qc.invalidateQueries({ queryKey: ['crew-schedule'] });
    },
  });
}
